import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'iu-banner',
  templateUrl: './banner.component.html',
  styleUrls: ['./banner.component.scss'],
  standalone: true,
  imports: [NgClass, NgTemplateOutlet, TranslateModule],
})
export default class BannerComponent implements OnInit, AfterViewInit, OnDestroy {
  /** Leave animation duration (ms), keep in sync with banner.component.scss */
  static readonly LEAVE_DURATION = 180;

  @HostBinding('style.display') display = 'block';

  @Input()
  type = 'error';

  @Input()
  icon: string;

  @Input()
  position: 'top' | 'bottom' | 'none' = 'none';

  @Input()
  closeable = false;

  /** Floating card layout (used by the workspace message stack) */
  @Input()
  toast = false;

  /** Short bold title shown above the message (toast layout only) */
  @Input()
  title: string;

  @Input()
  delay: number;
  closeDelay: NodeJS.Timeout;

  @ViewChild('toastText') toastTextRef: ElementRef<HTMLElement>;

  _hiddenMessage: string;
  #rawHiddenMessage: string;

  @Input()
  set hiddenMessage(value: string) {
    if (value && typeof value === 'string') {
      this.#rawHiddenMessage = value;
      this._hiddenMessage = value
        .split('\n')
        .map((line) => {
          const regexMatch = line.match(/\(.*:\d*:\d*\)/);
          if (regexMatch) {
            return (
              line.substring(0, regexMatch.index) +
              '<strong>' +
              line.substring(regexMatch.index, regexMatch.index + regexMatch[0].length) +
              '</strong>'
            );
          } else {
            return line;
          }
        })
        .join('\n');
    }
  }

  get hiddenMessage() {
    return this._hiddenMessage;
  }
  hiddenMessageDisplay = false;

  @Output()
  closeEmitter = new EventEmitter<any>();

  //#region toast state
  expanded = false;
  isOverflowing = false;
  copied = false;
  leaving = false;
  #closeCancelled = false;
  #remaining = 0;
  #startedAt = 0;
  #copiedTimeout: NodeJS.Timeout;
  #leaveTimeout: NodeJS.Timeout;
  //#endregion

  readonly #cdr = inject(ChangeDetectorRef);

  get severity(): 'success' | 'info' | 'warn' | 'error' {
    switch (this.type) {
      case 'success':
        return 'success';
      case 'message':
        return 'info';
      case 'warning':
        return 'warn';
      default:
        return 'error';
    }
  }

  get toastIcon(): string {
    if (this.icon !== undefined) {
      return this.icon;
    }
    switch (this.severity) {
      case 'success':
        return 'icon-check';
      case 'info':
        return 'icon-info';
      case 'warn':
        return 'icon-triangle-exclamation';
      default:
        return 'icon-blocked';
    }
  }

  ngOnInit() {
    if (this.delay && this.delay > 0) {
      this.#startTimer(this.delay);
    }
  }

  ngAfterViewInit() {
    if (this.toast) {
      // measure after the projected content is laid out
      setTimeout(() => this.#checkOverflow());
    }
  }

  ngOnDestroy() {
    clearTimeout(this.closeDelay);
    clearTimeout(this.#copiedTimeout);
    clearTimeout(this.#leaveTimeout);
  }

  /**
   * Ferme la banniere
   * @param {Event}event
   */
  closeBanner(event?: Event) {
    event?.stopPropagation();
    clearTimeout(this.closeDelay);
    this.closeDelay = undefined;
    if (this.toast) {
      if (this.leaving) {
        return;
      }
      this.leaving = true;
      this.#cdr.markForCheck();
      this.#leaveTimeout = setTimeout(() => this.#doClose(), BannerComponent.LEAVE_DURATION);
    } else {
      this.#doClose();
    }
  }

  /**
   * Annule le delai pour la disparition de la bannière
   * @param {Event}event
   */
  cancelCloseDelay(event: Event) {
    event?.stopPropagation();
    if (this.delay && this.delay > 0) {
      this.#closeCancelled = true;
      clearTimeout(this.closeDelay);
      this.closeDelay = undefined;
    }
  }

  /** Hover pauses the auto close, leaving resumes it with the remaining time */
  pauseCloseDelay() {
    if (this.closeDelay) {
      clearTimeout(this.closeDelay);
      this.closeDelay = undefined;
      this.#remaining -= Date.now() - this.#startedAt;
    }
  }

  resumeCloseDelay() {
    if (!this.#closeCancelled && !this.leaving && !this.closeDelay && this.delay > 0) {
      this.#startTimer(Math.max(this.#remaining, 1500));
    }
  }

  /**
   * Affiche le message caché de la bannière
   * @param {MouseEvent}event
   */
  showHiddenMessage(event: MouseEvent) {
    event.stopPropagation();
    this.cancelCloseDelay(event);
    if (event.altKey) {
      this.hiddenMessageDisplay = !this.hiddenMessageDisplay;
    }
  }

  toggleExpanded(event: Event) {
    this.cancelCloseDelay(event);
    this.expanded = !this.expanded;
  }

  copyMessage(event: Event) {
    this.cancelCloseDelay(event);
    const text = [this.title, this.toastTextRef?.nativeElement?.innerText?.trim(), this.#rawHiddenMessage]
      .filter((part) => !!part)
      .join('\n');
    navigator.clipboard
      ?.writeText(text)
      .then(() => {
        this.copied = true;
        this.#cdr.markForCheck();
        clearTimeout(this.#copiedTimeout);
        this.#copiedTimeout = setTimeout(() => {
          this.copied = false;
          this.#cdr.markForCheck();
        }, 1500);
      })
      .catch(() => {});
  }

  #startTimer(ms: number) {
    clearTimeout(this.closeDelay);
    this.#remaining = ms;
    this.#startedAt = Date.now();
    this.closeDelay = setTimeout(() => this.closeBanner(), ms);
  }

  #doClose() {
    this.display = 'none';
    this.closeEmitter.emit();
  }

  #checkOverflow() {
    const el = this.toastTextRef?.nativeElement;
    const overflowing = !!el && el.scrollHeight > el.clientHeight + 1;
    if (overflowing !== this.isOverflowing) {
      this.isOverflowing = overflowing;
      this.#cdr.markForCheck();
    }
  }
}
